import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import toastUtils from "../../utils/toastUtils";
import { i18n } from "../../translate/i18n";
import { format, parseISO } from "date-fns";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import ForbiddenPage from "../../components/ForbiddenPage";

// Material UI Components
import {
  Box,
  Typography,
  Chip,
  Avatar
} from "@mui/material";

// Material UI Icons
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  DeleteOutline as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  CloudSync as SyncIcon,
  AdminPanelSettings as AdminIcon,
  People as ParticipantIcon,
  PersonAdd as PersonAddIcon,
  Info as InfoIcon,
  GetApp as ExtractIcon,
  AutoAwesome as AutoIcon,
  Summarize as SummarizeIcon,
  Dashboard as DashboardIcon,
  Timeline as TimelineIcon,
  FileUpload as FileUploadIcon,
  Assignment as TicketIcon
} from "@mui/icons-material";

// Context
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

// Standard Components
import StandardPageLayout from "../../components/shared/StandardPageLayout";
import StandardDataTable from "../../components/shared/StandardDataTable";
import StandardModal from "../../components/shared/StandardModal";
import StandardEmptyState from "../../components/shared/StandardEmptyState";
import StandardButton from "../../components/shared/StandardButton";
import StandardTabContent from "../../components/shared/StandardTabContent";

// Components específicos
import CreateGroupModal from "./components/CreateGroupModal";
import GroupInfoModal from "./components/GroupInfoModal";
import JoinGroupModal from "./components/JoinGroupModal";
import GroupRequestsModal from "./components/GroupRequestsModal";
import SyncGroupsModal from "./components/SyncGroupsModal";
import ExtractContactsTab from "./components/ExtractContactsTab";
import ImportContactsTab from "./components/ImportContactsTab";
import ExtractContactsFromGroupModal from "./components/ExtractContactsFromGroupModal";
import GroupSummaryModal from "../../components/GroupSummaryModal";
import GroupSeriesTab from "./components/GroupSeriesTab";

// Services
import api from "../../services/api";

const Groups = () => {
  const { user, socket } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const history = useHistory();
  const { permissions, loading: permissionsLoading } = usePlanPermissions();

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openJoinModal, setOpenJoinModal] = useState(false);
  const [openRequestsModal, setOpenRequestsModal] = useState(false);
  const [openSyncModal, setOpenSyncModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmForceDelete, setConfirmForceDelete] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [needsSync, setNeedsSync] = useState(false);
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [openExtractModal, setOpenExtractModal] = useState(false);
  const [seriesStats, setSeriesStats] = useState(null);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const [selectedGroupForSummary, setSelectedGroupForSummary] = useState(null);
  const [ticketsCreatedInSync, setTicketsCreatedInSync] = useState(0);

  // Debounce para pesquisa
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchGroups = useCallback(async (page = 1, search = "", reset = true) => {
    if (page === 1) setLoading(true);

    try {
      const { data } = await api.get("/groups", {
        params: {
          searchParam: search,
          pageNumber: page,
          limit: 20
        },
      });

      if (reset || page === 1) {
        setGroups(data.groups);
      } else {
        setGroups(prev => [...prev, ...data.groups]);
      }

      setTotalGroups(data.count);
      setHasMore(data.hasMore);
      setPageNumber(page);

      if (data.count === 0 && whatsappConnections.length > 0) {
        setNeedsSync(true);
      } else {
        setNeedsSync(false);
      }
    } catch (err) {
      toastUtils.error(err, "Erro ao Carregar");
    } finally {
      setLoading(false);
    }
  }, [whatsappConnections.length]);

  const fetchSeriesStats = useCallback(async () => {
    try {
      const { data } = await api.get("/group-series");
      const stats = {
        totalSeries: data.length,
        activeSeries: data.filter(s => s.autoCreateEnabled).length,
        inactiveSeries: data.filter(s => !s.autoCreateEnabled).length
      };
      setSeriesStats(stats);
    } catch (err) {
      console.log("Erro ao carregar estatísticas das séries:", err);
    }
  }, []);

  useEffect(() => {
    checkWhatsAppConnections();
    fetchSeriesStats();
  }, [fetchSeriesStats]);

  useEffect(() => {
    if (whatsappConnections.length >= 0) {
      fetchGroups(1, searchParam);
    }
  }, [whatsappConnections, fetchGroups]);

  useEffect(() => {

    if (socket && typeof socket.on === 'function' && typeof socket.off === 'function') {
      const handleGroupUpdate = (data) => {
        if (data.action === "create") {
          setGroups((prevGroups) => [data.group, ...prevGroups]);
          setTotalGroups((prevTotal) => prevTotal + 1);
        }

        if (data.action === "update") {
          setGroups((prevGroups) =>
            prevGroups.map((group) => {
              if (group.id === data.group.id) {
                return { ...group, ...data.group };
              }
              return group;
            })
          );
        }

        if (data.action === "delete") {
          setGroups((prevGroups) =>
            prevGroups.filter((group) => group.id !== data.groupId)
          );
          setTotalGroups((prevTotal) => Math.max(prevTotal - 1, 0));
        }
      };

      const handleGroupSeriesUpdate = (data) => {
        fetchSeriesStats();

        if (data.action === "new_group_created") {
          toastUtils.success(`Novo grupo criado automaticamente: ${data.newGroup.name}`, "Grupo Criado");
        }
      };

      // ✅ NOVO: Listener para tickets criados durante sincronização
      const handleTicketUpdate = (data) => {
        if (data.action === "create") {
          // Verificar se o ticket é de um grupo (contact com isGroup=true)
          if (data.ticket && data.ticket.contact && data.ticket.contact.isGroup) {
            console.log("Ticket de grupo criado durante sincronização:", data.ticket);

            // Incrementar contador temporário
            setTicketsCreatedInSync(prev => prev + 1);

            // Notificação individual (opcional, pode ser removida se for muito verbosa)
            // toast.info(`Ticket criado para grupo: ${data.ticket.contact.name}`, { 
            //   autoClose: 2000 
            // });
          }
        }

        if (data.action === "update") {
          // Verificar se é update de ticket de grupo para status pending
          if (data.ticket && data.ticket.contact && data.ticket.contact.isGroup && data.ticket.status === "pending") {
            console.log("Ticket de grupo atualizado durante sincronização:", data.ticket);
          }
        }
      };

      // ✅ NOVO: Listener específico para sincronização de grupos
      const handleSyncGroups = (data) => {
        if (data.action === "start") {
          // Reset contador quando iniciar nova sincronização
          setTicketsCreatedInSync(0);
        }

        if (data.action === "complete") {
          // Notificação final sobre tickets
          if (data.result && (data.result.ticketsCreated > 0 || data.result.ticketsUpdated > 0)) {
            const ticketMessages = [];

            if (data.result.ticketsCreated > 0) {
              ticketMessages.push(`${data.result.ticketsCreated} ticket(s) criado(s)`);
            }

            if (data.result.ticketsUpdated > 0) {
              ticketMessages.push(`${data.result.ticketsUpdated} ticket(s) reaberto(s)`);
            }

            // Toast final com resumo
            toastUtils.success(
              `Sincronização concluída! ${ticketMessages.join(' e ')}. Verifique sua lista de atendimentos.`,
              "Sincronização Concluída",
              {
                autoClose: 5000,
                onClick: () => {
                  // ✅ Redirecionar para a página de tickets quando clicar no toast
                  history.push('/tickets');
                }
              }
            );
          }

          // Reset contador após sincronização
          setTimeout(() => {
            setTicketsCreatedInSync(0);
          }, 3000);
        }
      };

      try {
        socket.on("group", handleGroupUpdate);
        socket.on("group-series", handleGroupSeriesUpdate);
        socket.on("auto-group-created", handleGroupSeriesUpdate);
        // ✅ NOVOS listeners
        socket.on(`company-${user.companyId}-ticket`, handleTicketUpdate);
        socket.on("sync-groups", handleSyncGroups);

        return () => {
          try {
            socket.off("group", handleGroupUpdate);
            socket.off("group-series", handleGroupSeriesUpdate);
            socket.off("auto-group-created", handleGroupSeriesUpdate);
            // ✅ NOVOS cleanup
            socket.off(`company-${user.companyId}-ticket`, handleTicketUpdate);
            socket.off("sync-groups", handleSyncGroups);
          } catch (error) {
            console.error('Erro ao remover listeners do socket em Groups:', error);
          }
        };
      } catch (error) {
        console.error('Erro ao adicionar listeners do socket em Groups:', error);
      }
    }
  }, [user.companyId, socketManager, fetchSeriesStats, history]);

  const handleOpenSummaryModal = (group) => {
    setSelectedGroupForSummary(group);
    setOpenSummaryModal(true);
  };

  const handleCloseSummaryModal = () => {
    setSelectedGroupForSummary(null);
    setOpenSummaryModal(false);
  };

  const checkWhatsAppConnections = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      const connectedWhatsApps = data.filter(w => w.status === "CONNECTED");
      setWhatsappConnections(connectedWhatsApps);

      if (connectedWhatsApps.length === 0) {
        setNeedsSync(false);
      }
    } catch (err) {
      console.error("Erro ao verificar conexões WhatsApp:", err);
    }
  };

  // Funções auxiliares
  const getParticipantsCount = (group) => {
    try {
      // Log para debug
      console.log(`[getParticipantsCount] Grupo ${group.subject}:`, {
        participantsJson: group.participantsJson,
        tipo: typeof group.participantsJson,
        isArray: Array.isArray(group.participantsJson),
        length: group.participantsJson && group.participantsJson.length,
        currentParticipantCount: group.currentParticipantCount
      });
  
      // ✅ CORREÇÃO: Usar campo calculado do backend se disponível
      if (typeof group.currentParticipantCount === 'number') {
        return group.currentParticipantCount;
      }
  
      // ✅ CORREÇÃO: Validação mais robusta do participantsJson
      if (!group.participantsJson) {
        console.warn(`[getParticipantsCount] participantsJson é null/undefined para grupo ${group.subject}`);
        return 0;
      }
  
      if (Array.isArray(group.participantsJson)) {
        return group.participantsJson.length;
      }
  
      // ✅ NOVO: Tentar parsear se for string
      if (typeof group.participantsJson === 'string') {
        try {
          const parsed = JSON.parse(group.participantsJson);
          if (Array.isArray(parsed)) {
            console.log(`[getParticipantsCount] participantsJson parseado de string para grupo ${group.subject}: ${parsed.length} participantes`);
            return parsed.length;
          }
        } catch (parseError) {
          console.error(`[getParticipantsCount] Erro ao parsear participantsJson string para grupo ${group.subject}:`, parseError);
        }
      }
  
      console.warn(`[getParticipantsCount] participantsJson tem formato inesperado para grupo ${group.subject}:`, typeof group.participantsJson);
      return 0;
  
    } catch (error) {
      console.error(`[getParticipantsCount] Erro ao contar participantes do grupo ${group.subject}:`, error);
      return 0;
    }
  };
  

  const getGroupInitials = (name) => {
    if (!name) return "GP";
    const words = name.split(" ");
    if (words.length === 1) return name.substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const getUserRole = (group) => {
    try {
      if (group.userRole) {
        return group.userRole;
      }

      if (!group.participantsJson || !Array.isArray(group.participantsJson)) {
        return "unknown";
      }

      const hasAdmins = group.participantsJson.some(p =>
        p.admin === 'admin' || p.admin === 'superadmin' || p.isAdmin === true
      );

      if (!hasAdmins) {
        return "participant";
      }

      const adminParticipants = group.participantsJson.filter(p =>
        p.admin === 'admin' || p.admin === 'superadmin' || p.isAdmin === true
      );

      const adminRatio = adminParticipants.length / group.participantsJson.length;

      if (adminRatio > 0.5) {
        return "participant";
      }

      return group.userRole || "participant";
    } catch (error) {
      console.error("Erro ao verificar role do usuário:", error);
      return "unknown";
    }
  };

  // Configuração das colunas da tabela
  const columns = [
    {
      id: 'name',
      field: 'subject',
      label: i18n.t("groups.table.name"),
      render: (group) => (
        <Box display="flex" alignItems="center">
          <Avatar
            src={group.profilePic ? `${process.env.REACT_APP_BACKEND_URL}${group.profilePic}` : undefined}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: typeof group.id === 'string' ?
                `hsl(${group.id.charCodeAt(0) * 10}, 70%, 50%)` :
                '#128C7E',
              marginRight: 2,
              border: '1px solid rgba(0, 0, 0, 0.12)'
            }}
          >
            {getGroupInitials(group.subject)}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {group.subject || i18n.t("groups.noGroupName")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {group.jid ? group.jid.split('@')[0] : ''}
            </Typography>
            {group.isManaged && (
              <Chip
                label={i18n.t("groups.stats.automatic")}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ ml: 1, height: 20 }}
              />
            )}
          </Box>
        </Box>
      )
    },
    {
      id: 'permission',
      field: 'userRole',
      label: i18n.t("groups.table.permission"),
      render: (group) => {
        const userRole = getUserRole(group);

        if (userRole === "admin") {
          return (
            <Chip
              icon={<AdminIcon />}
              label={i18n.t("groups.permissions.admin")}
              size="small"
              color="warning"
              variant="outlined"
            />
          );
        }

        if (userRole === "participant") {
          return (
            <Chip
              icon={<ParticipantIcon />}
              label={i18n.t("groups.permissions.participant")}
              size="small"
              color="default"
              variant="outlined"
            />
          );
        }

        return (
          <Chip
            label={i18n.t("groups.permissions.unknown")}
            size="small"
            color="error"
            variant="outlined"
          />
        );
      }
    },
    {
      id: 'participants',
      field: 'participantsJson',
      label: i18n.t("groups.table.participants"),
      render: (group) => (
        <Box display="flex" alignItems="center">
          <ParticipantIcon
            fontSize="small"
            color="action"
            sx={{ mr: 0.5 }}
          />
          <Typography variant="body2">
            {getParticipantsCount(group)}
            {group.isManaged && group.maxParticipants && (
              <span style={{ color: 'text.secondary' }}>
                /{group.maxParticipants}
              </span>
            )}
          </Typography>
        </Box>
      )
    },
    {
      id: 'createdAt',
      field: 'createdAt',
      label: i18n.t("groups.table.createdAt"),
      render: (group) => {
        try {
          return group.createdAt && group.createdAt !== 'Invalid Date'
            ? format(parseISO(group.createdAt), "dd/MM/yyyy HH:mm")
            : "";
        } catch (error) {
          console.warn('Erro ao formatar data:', group.createdAt, error);
          return "";
        }
      }
    }
  ];

  // Ações da tabela - condicionais baseadas no role do usuário
  const getTableActions = (group) => {
    const userRole = getUserRole(group);
    const actions = [
      {
        label: i18n.t("groups.actions.view") + "/" + i18n.t("groups.actions.edit"),
        icon: <InfoIcon />,
        onClick: (group) => handleOpenInfoModal(group),
        color: "primary"
      },
      {
        label: i18n.t("groups.actions.extract"),
        icon: <ExtractIcon />,
        onClick: (group) => handleOpenExtractModal(group),
        color: "primary"
      },
      {
        label: i18n.t("aiSuggestions.groupSummaryTitle"),
        icon: <SummarizeIcon />,
        onClick: (group) => handleOpenSummaryModal(group),
        color: "primary"
      }
    ];

    if (userRole === "admin") {
      actions.push(
        {
          label: i18n.t("groups.actions.requests"),
          icon: <PersonAddIcon />,
          onClick: (group) => handleOpenRequestsModal(group),
          color: "primary"
        },
        {
          label: i18n.t("groups.actions.leaveGroup"),
          icon: <DeleteIcon />,
          onClick: (group) => handleOpenDeleteConfirm(group),
          color: "error"
        },
        {
          label: i18n.t("groups.actions.removeFromSystem"),
          icon: <DeleteForeverIcon />,
          onClick: (group) => handleOpenForceDeleteConfirm(group),
          color: "error"
        }
      );
    } else if (userRole === "participant") {
      actions.push({
        label: i18n.t("groups.actions.removeFromSystem"),
        icon: <DeleteForeverIcon />,
        onClick: (group) => handleOpenForceDeleteConfirm(group),
        color: "error"
      });
    }

    return actions;
  };

  // Handlers dos modais e ações
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      checkWhatsAppConnections(),
      fetchGroups(1, searchParam, true),
      fetchSeriesStats()
    ]);
    setRefreshing(false);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchParam(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const newTimeout = setTimeout(() => {
      fetchGroups(1, value, true);
    }, 500);

    setSearchTimeout(newTimeout);
  };

  const handleChangeTab = (event, newValue) => {
    setTab(newValue);
  };

  const handleOpenExtractModal = (group) => {
    setSelectedGroup(group);
    setOpenExtractModal(true);
  };

  const handleCloseExtractModal = () => {
    setSelectedGroup(null);
    setOpenExtractModal(false);
  };

  const handleOpenSyncModal = () => {
    setOpenSyncModal(true);
  };

  const handleCloseSyncModal = () => {
    setOpenSyncModal(false);
  };

  const handleSyncComplete = () => {
    setNeedsSync(false);
    fetchGroups(1, searchParam, true);
    // ✅ Removido toast daqui pois já é exibido via Socket.io
    // toast.success("Grupos sincronizados com sucesso!");
  };

  const handleOpenCreateModal = () => {
    setOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    fetchGroups(1, searchParam, true);
  };

  const handleOpenInfoModal = (group) => {
    setSelectedGroup(group);
    setOpenInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setSelectedGroup(null);
    setOpenInfoModal(false);
    fetchGroups(1, searchParam, true);
  };

  const handleOpenJoinModal = () => {
    setOpenJoinModal(true);
  };

  const handleCloseJoinModal = () => {
    setOpenJoinModal(false);
    fetchGroups(1, searchParam, true);
  };

  const handleOpenRequestsModal = (group) => {
    setSelectedGroup(group);
    setOpenRequestsModal(true);
  };

  const handleCloseRequestsModal = () => {
    setSelectedGroup(null);
    setOpenRequestsModal(false);
  };

  const handleOpenDeleteConfirm = (group) => {
    setSelectedGroup(group);
    setConfirmDelete(true);
  };

  const handleCloseDeleteConfirm = () => {
    setSelectedGroup(null);
    setConfirmDelete(false);
  };

  const handleOpenForceDeleteConfirm = (group) => {
    setSelectedGroup(group);
    setConfirmForceDelete(true);
  };

  const handleCloseForceDeleteConfirm = () => {
    setSelectedGroup(null);
    setConfirmForceDelete(false);
  };

  const handleDeleteGroup = async () => {
    try {
      await api.delete(`/groups/${selectedGroup.id}`);
      toastUtils.success(i18n.t("groups.groupDeleted"), "Grupo Removido");
      handleCloseDeleteConfirm();
      fetchGroups(1, searchParam, true);
    } catch (err) {
      toastUtils.error(err, "Erro ao Remover");
    }
  };

  const handleForceDeleteGroup = async () => {
    try {
      await api.delete(`/groups/${selectedGroup.id}?forceDelete=true`);
      toastUtils.success(i18n.t("groups.groupForceDeleted"), "Grupo Excluído");
      handleCloseForceDeleteConfirm();
      fetchGroups(1, searchParam, true);
    } catch (err) {
      toastUtils.error(err, "Erro ao Excluir");
    }
  };

  const getGroupStats = () => {
    const adminGroups = groups.filter(group => {
      return getUserRole(group) === "admin";
    }).length;

    const participantGroups = groups.filter(group => {
      return getUserRole(group) === "participant";
    }).length;

    const managedGroups = groups.filter(group => group.isManaged).length;

    return { adminGroups, participantGroups, managedGroups };
  };

  const { adminGroups, participantGroups, managedGroups } = getGroupStats();

  // Cleanup timeout na desmontagem
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Verificar permissão para usar Grupos
  if (!permissionsLoading && !permissions.useGroups) {
    return <ForbiddenPage />;
  }

  // Configuração das ações do cabeçalho
  const pageActions = [
    ...(whatsappConnections.length > 0 ? [{
      label: i18n.t("groups.actions.sync"),
      icon: <SyncIcon />,
      onClick: handleOpenSyncModal,
      variant: "outlined",
      color: "primary"
    }] : []),
    ...user.profile === "admin" ? [{
      label: i18n.t("groups.newGroup"),
      icon: <AddIcon />,
      onClick: handleOpenCreateModal,
      variant: "contained",
      color: "primary"
    }] : []
  ];

  // Configuração das abas
  const tabs = [
    {
      label: i18n.t("groups.tabs.list"),
      icon: <GroupIcon />
    },
    {
      label: i18n.t("groups.tabs.automaticSeries"),
      icon: <AutoIcon />
    },
    {
      label: i18n.t("groups.tabs.extractContacts"),
      icon: <ExtractIcon />
    },
    {
      label: i18n.t("groups.tabs.importContacts"),
      icon: <FileUploadIcon />
    },
    {
      label: i18n.t("groups.tabs.requests"),
      icon: <PersonAddIcon />
    }
  ];

  const renderSyncAlert = () => {
    if (!needsSync || whatsappConnections.length === 0) return null;

    return {
      severity: "info",
      title: i18n.t("groups.alerts.syncTitle"),
      message: i18n.t("groups.alerts.syncMessage", { count: whatsappConnections.length }),
      action: (
        <StandardButton
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={handleOpenSyncModal}
          size="small"
        >
          {i18n.t("groups.actions.sync")}
        </StandardButton>
      )
    };
  };

  const renderWhatsAppAlert = () => {
    if (whatsappConnections.length === 0) {
      return {
        severity: "warning",
        title: i18n.t("groups.alerts.noWhatsAppTitle"),
        message: i18n.t("groups.alerts.noWhatsAppMessage")
      };
    }
    return null;
  };

  // ✅ NOVO: Alert para tickets criados em tempo real
  const renderTicketsAlert = () => {
    if (ticketsCreatedInSync === 0) return null;

    return {
      severity: "success",
      title: i18n.t("groups.alerts.ticketsCreatedTitle"),
      message: i18n.t("groups.alerts.ticketsCreatedMessage", { count: ticketsCreatedInSync }),
      action: (
        <StandardButton
          variant="outlined"
          startIcon={<TicketIcon />}
          onClick={() => history.push('/tickets')}
          size="small"
          color="success"
        >
          {i18n.t("groups.toasts.viewTickets")}
        </StandardButton>
      )
    };
  };

  const renderTabContent = () => {
    switch (tab) {
      case 0: // Lista de Grupos
        const alerts = [];
        const syncAlert = renderSyncAlert();
        const whatsappAlert = renderWhatsAppAlert();
        const ticketsAlert = renderTicketsAlert();

        if (syncAlert) alerts.push(syncAlert);
        if (whatsappAlert) alerts.push(whatsappAlert);
        if (ticketsAlert) alerts.push(ticketsAlert);

        return (
          <StandardTabContent
            alerts={alerts}
            variant="default"
          >
            <StandardDataTable
              data={groups}
              columns={columns}
              loading={loading}
              actions={getTableActions}
              stickyHeader={false}
              size="medium"
              hover={true}
              maxVisibleActions={3}
              emptyIcon={<GroupIcon />}
              emptyTitle={i18n.t("groups.noGroupsFound")}
              emptyDescription={i18n.t("groups.emptyStates.noGroups.description")}
              emptyActionLabel={whatsappConnections.length > 0 ? i18n.t("groups.emptyStates.noGroups.actionLabel") : undefined}
              onEmptyActionClick={whatsappConnections.length > 0 ? handleOpenSyncModal : undefined}
            />
          </StandardTabContent>
        );

      case 1: // Séries de Grupos Automáticos
        return <GroupSeriesTab />;

      case 2: // Extração de Contatos
        return <ExtractContactsTab />;

      case 3: // Importação de Contatos
        return <ImportContactsTab />;

      case 4: // Solicitações
        return (
          <StandardTabContent
            title={i18n.t("groups.emptyStates.requests.title")}
            description={i18n.t("groups.tabs.requests")}
            icon={<PersonAddIcon />}
            variant="padded"
          >
            <StandardEmptyState
              type="default"
              icon={<PersonAddIcon />}
              title={i18n.t("groups.emptyStates.requests.title")}
              description={i18n.t("groups.selectGroupToSeeRequests")}
            />
          </StandardTabContent>
        );

      default:
        return null;
    }
  };

  // Título da página com estatísticas
  const pageTitle = (
    <Box display="flex" alignItems="center" gap={1}>
      <GroupIcon />
      {i18n.t("groups.title")}
      {groups.length > 0 && (
        <Box display="flex" gap={1} ml={2}>
          <Chip
            icon={<AdminIcon />}
            label={`${adminGroups} ${i18n.t("groups.stats.admin")}`}
            size="small"
            color="warning"
            variant="outlined"
          />
          <Chip
            icon={<ParticipantIcon />}
            label={`${participantGroups} ${i18n.t("groups.stats.participant")}`}
            size="small"
            color="default"
            variant="outlined"
          />
          {managedGroups > 0 && (
            <Chip
              icon={<AutoIcon />}
              label={`${managedGroups} ${i18n.t("groups.stats.automatic")}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {seriesStats && (
            <Chip
              icon={<TimelineIcon />}
              label={`${seriesStats.activeSeries}/${seriesStats.totalSeries} ${i18n.t("groups.stats.series")}`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
          {/* ✅ NOVO: Chip para tickets criados em tempo real */}
          {ticketsCreatedInSync > 0 && (
            <Chip
              icon={<TicketIcon />}
              label={`${ticketsCreatedInSync} ${i18n.t("groups.toasts.viewTickets")}`}
              size="small"
              color="success"
              variant="filled"
              onClick={() => history.push('/tickets')}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <StandardPageLayout
        title={pageTitle}
        actions={pageActions}
        searchValue={searchParam}
        onSearchChange={handleSearch}
        searchPlaceholder={i18n.t("groups.searchPlaceholder")}
        showSearch={tab === 0}
        tabs={tabs}
        activeTab={tab}
        onTabChange={handleChangeTab}
        loading={loading && tab === 0}
      >
        <div style={{ height: '100%' }}>
          {renderTabContent()}
        </div>
      </StandardPageLayout>

      {/* Modais */}
      <SyncGroupsModal
        open={openSyncModal}
        onClose={handleCloseSyncModal}
        onComplete={handleSyncComplete}
      />

      {selectedGroup && (
        <ExtractContactsFromGroupModal
          open={openExtractModal}
          onClose={handleCloseExtractModal}
          group={selectedGroup}
        />
      )}

      {selectedGroupForSummary && (
        <GroupSummaryModal
          open={openSummaryModal}
          onClose={handleCloseSummaryModal}
          ticket={{
            id: selectedGroupForSummary.id,
            isGroup: true,
            contact: {
              name: selectedGroupForSummary.subject,
              isGroup: true
            }
          }}
        />
      )}

      <CreateGroupModal
        open={openCreateModal}
        onClose={handleCloseCreateModal}
      />

      {selectedGroup && (
        <GroupInfoModal
          open={openInfoModal}
          onClose={handleCloseInfoModal}
          group={selectedGroup}
        />
      )}

      <JoinGroupModal
        open={openJoinModal}
        onClose={handleCloseJoinModal}
      />

      {selectedGroup && (
        <GroupRequestsModal
          open={openRequestsModal}
          onClose={handleCloseRequestsModal}
          group={selectedGroup}
        />
      )}

      {/* Confirmar exclusão */}
      <StandardModal
        open={confirmDelete}
        onClose={handleCloseDeleteConfirm}
        title={i18n.t("groups.deleteConfirmTitle")}
        icon={DeleteIcon}
        actions={[
          {
            label: i18n.t("cancel"),
            onClick: handleCloseDeleteConfirm,
            variant: "outlined",
            color: "primary"
          },
          {
            label: i18n.t("delete"),
            onClick: handleDeleteGroup,
            variant: "contained",
            color: "error",
            startIcon: <DeleteIcon />
          }
        ]}
      >
        <Typography>
          {i18n.t("groups.deleteConfirmMessage", { name: selectedGroup && selectedGroup.subject })}
        </Typography>
      </StandardModal>

      {/* Confirmar exclusão forçada */}
      <StandardModal
        open={confirmForceDelete}
        onClose={handleCloseForceDeleteConfirm}
        title={i18n.t("groups.forceDeleteConfirmTitle")}
        icon={DeleteForeverIcon}
        iconColor="#d32f2f"
        actions={[
          {
            label: i18n.t("cancel"),
            onClick: handleCloseForceDeleteConfirm,
            variant: "outlined",
            color: "primary"
          },
          {
            label: i18n.t("groups.forceDelete"),
            onClick: handleForceDeleteGroup,
            variant: "contained",
            color: "error",
            startIcon: <DeleteForeverIcon />
          }
        ]}
      >
        <Typography variant="body1" paragraph>
          {i18n.t("groups.forceDeleteConfirmMessage", { name: selectedGroup && selectedGroup.subject })}
        </Typography>
        <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
          {i18n.t("groups.forceDeleteWarning")}
        </Typography>
      </StandardModal>
    </>
  );
};

export default Groups;